import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { theme, buttonClass } from '../ui/theme';

/**
 * PWA Install Prompt Component
 * Shows an install prompt when users visit the site if the browser supports installation and the app is not yet installed
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false); // Permanently hide prompt when checked

  useEffect(() => {
    // Check if already installed (running in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');
    setIsInstalled(isStandalone);

    // Permanently dismissed
    const dismissedForever = localStorage.getItem('pwa-install-dismissed-forever');
    if (dismissedForever === '1') {
      setIsDismissed(true);
      return;
    }

    // Check if prompt was previously dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissTime = parseInt(dismissed, 10);
      // If dismissed more than 7 days ago, show prompt again
      if (Date.now() - dismissTime < 7 * 24 * 60 * 60 * 1000) {
        setIsDismissed(true);
      }
    }

    // Listen for beforeinstallprompt event (Chrome/Edge)
    const handleBeforeInstallPrompt = (e) => {
      // Prevent default install prompt
      e.preventDefault();
      // Save event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    // When "don't show again" is checked: remember even if user clicks install (regardless of user's choice in system dialog)
    if (dontShowAgain) {
      localStorage.setItem('pwa-install-dismissed-forever', '1');
      try { localStorage.removeItem('pwa-install-dismissed'); } catch (_) {}
      setIsDismissed(true);
    }

    if (!deferredPrompt) {
      // If no deferredPrompt, might be Safari or other browsers
      // Show manual install instructions
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
        alert(t('installPrompt.installIOS'));
      } else if (userAgent.includes('android') && userAgent.includes('chrome')) {
        alert(t('installPrompt.installAndroid'));
      } else {
        alert(t('installPrompt.installGeneric'));
      }
      return;
    }

    // Show install prompt
    deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted install prompt');
    } else {
      console.log('User dismissed install prompt');
    }

    // Clear saved prompt
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsInstallable(false);
    // If "don't show again" is checked, dismiss forever, otherwise use 7-day strategy
    if (dontShowAgain) {
      localStorage.setItem('pwa-install-dismissed-forever', '1');
      try { localStorage.removeItem('pwa-install-dismissed'); } catch (_) {}
    } else {
      // Save dismiss time
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
  };

  // Don't show if already installed, dismissed, or not installable
  if (isInstalled || isDismissed || !isInstallable) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
      <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-xl border border-blue-400/25 p-4 animate-slide-up text-white'].join(' ')}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg 
              className="w-8 h-8 text-blue-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" 
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white mb-1">
              {t('installPrompt.title')}
            </h3>
            <p className="text-sm text-white/70 mb-3">
              {t('installPrompt.description')}
            </p>
            <label className="flex items-center gap-2 mb-3 select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <span className="text-xs text-white/60">
                {t('installPrompt.dontShowAgain')}
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleInstall}
                className={[buttonClass({ variant: 'outline', size: 'md' }), 'flex-1'].join(' ')}
              >
                {t('installPrompt.install')}
              </button>
              <button
                onClick={handleDismiss}
                className={buttonClass({ variant: 'ghost', size: 'md' })}
              >
                {t('installPrompt.later')}
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-white/55 hover:text-white transition-colors"
            aria-label={t('installPrompt.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
